import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useApi } from '@axiom/ui';

import type { ChatHistoryItem } from '../../api/chat-api';
import { ChatApi } from '../../api/chat-api';
import { ChatErrorUtil } from '../../utils/chat-error-util';
import { ChatNoticeUtil } from '../../utils/chat-notice-util';
import type { ChatNotice } from '../../types/chat.types';

/**
 * useChatHistoryData - I own the sidebar's list of conversations. ONLY concerned with that list
 * and the writes that edit it - no transcript, no sending.
 */
export const useChatHistoryData = ({
  setNotice,
}: {
  setNotice: Dispatch<SetStateAction<ChatNotice | null>>;
}) => {
  /*
    **`useApi`, not state seeded by one `toPromise()` on mount**: the read is cached by endpoint,
    so on a revisit `watch()` fires no request and `toPromise()` waits on a `Subject` that never
    emits again - the sidebar came back empty and stayed that way until a hard refresh. `useApi`
    reads that cache synchronously instead, which is why the writes below re-read the list rather
    than patching a local copy of it.
  */
  const [{ data: chatHistory }] = useApi(ChatApi.readChatHistory());

  /*
    Dropped on the way out, so coming back reads the server rather than the cache. Conversations
    are renamed and hidden from other places, and this list is the one thing here with no way to
    notice - see `ChatApi.forgetChatHistory` for why it is `clear` and not `invalidate`.
  */
  useEffect(() => {
    return () => {
      ChatApi.forgetChatHistory();
    };
  }, []);

  /**
   * A send that minted a conversation. Nothing is passed: the list is ordered `updatedAt`
   * descending, so re-reading it puts the new conversation on top under its server-side name.
   */
  const refreshChatHistory = async () => ChatApi.refreshChatHistory();

  /*
    Every write here ends the same way: the list on screen is the server's, so it is re-read rather
    than patched, and it is awaited so a row's spinner runs until the list it moves in has landed.
    The catch covers the re-read too - a write that lands under a list that did not is still a
    stale screen, which is what the notice is for.
  */
  /**
   * **Reports whether the write landed**, because a rename that did not has a field to keep open -
   * see `ChatHistoryRename`. Hiding has nothing to keep, and ignores it.
   */
  const writeThenRefresh = async ({
    failureNotice,
    lockedNotice,
    write,
  }: {
    failureNotice: string;
    /** What a 423 says, which differs by whether the write can simply be repeated. */
    lockedNotice: string;
    write: () => Promise<unknown>;
  }) => {
    try {
      await write();
      await ChatApi.refreshChatHistory();

      return true;
    } catch (error) {
      /*
        A lock is the one failure here worth naming: it says the conversation is busy rather than
        broken, so trying again in a moment is the answer. Everything else the api declares on these
        writes is a diagnostic, and keeps the generic wording.
      */
      if (ChatErrorUtil.isLocked({ error })) {
        setNotice(lockedNotice);

        return false;
      }

      /*
        Nothing to be done about these now, so they say come back later and arrive as an error
        rather than a caution - unlike a lock, which clears on its own.
      */
      setNotice(
        ChatErrorUtil.isStopper({ error })
          ? ChatNoticeUtil.stopper()
          : failureNotice
      );

      return false;
    }
  };

  const hideChat = async ({ chatId }: { chatId: ChatHistoryItem['id'] }) =>
    writeThenRefresh({
      failureNotice: "We couldn't hide that chat. Please try again.",
      lockedNotice: ChatNoticeUtil.lockedHide(),
      write: async () => ChatApi.hideChat({ chatId }),
    });

  const unhideChat = async ({ chatId }: { chatId: ChatHistoryItem['id'] }) =>
    writeThenRefresh({
      failureNotice: "We couldn't unhide that chat. Please try again.",
      lockedNotice: ChatNoticeUtil.lockedUnhide(),
      write: async () => ChatApi.updateChatVisibility({ chatId }),
    });

  const renameChat = async ({
    chatId,
    name,
  }: {
    chatId: ChatHistoryItem['id'];
    name: string;
  }) =>
    // The response's own `name` is not read: the re-read is the same answer from the same server.
    writeThenRefresh({
      failureNotice: "We couldn't rename that chat. Please try again.",
      lockedNotice: ChatNoticeUtil.lockedRename(),
      write: async () => ChatApi.renameChat({ chatId, name }),
    });

  return {
    chatHistory,
    hideChat,
    refreshChatHistory,
    renameChat,
    unhideChat,
  };
};
